<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { jwtDecode } from 'jwt-decode'
import { Roles } from '@/constants.ts'
import jwtService from '@/services/jwtService.ts'
import type { JwtPayload } from '@/types/auth'
import invitationsHttpService from '@/services/http/invitationsHttpService.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t } = useI18n()

const smsBalanceQuery = useQuery({
  queryKey: ['admin', 'sms', 'balance'],
  queryFn: () => invitationsHttpService.getSmsBalance(),
})

const smsBalance = computed(() => smsBalanceQuery.data.value?.balance ?? null)

// Дашборды Hangfire/Serilog UI доступны только админам (бэкенд-гейт по роли Admin).
const isAdmin = computed(() => {
  const token = jwtService.getToken()
  if (!token) return false
  const raw = jwtDecode<JwtPayload>(token).role
  const roles = raw == null ? [] : Array.isArray(raw) ? raw : [raw]
  return roles.includes(Roles.Admin)
})
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <h1 class="text-xl font-bold">{{ t('dashboard.title') }}</h1>

    <Card class="sm:max-w-xs">
      <template #content>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-500">{{ t('dashboard.smsBalance.label') }}</span>
          <span v-if="smsBalanceQuery.isLoading.value" class="text-2xl font-bold">
            <i class="pi pi-spin pi-spinner" />
          </span>
          <span v-else-if="smsBalance != null" class="text-2xl font-bold">{{ smsBalance }}</span>
          <span v-else class="text-sm text-surface-500">{{
            t('dashboard.smsBalance.unavailable')
          }}</span>
        </div>
      </template>
    </Card>

    <Card v-if="isAdmin" class="sm:max-w-xs">
      <template #content>
        <div class="flex flex-col gap-2">
          <span class="text-sm text-surface-500">{{ t('dashboard.tools.title') }}</span>
          <a
            href="/api/hangfire"
            target="_blank"
            rel="noopener"
            class="text-primary hover:underline flex items-center gap-2"
          >
            <i class="pi pi-server" /> {{ t('dashboard.tools.hangfire') }}
          </a>
          <a
            href="/api/serilog-ui"
            target="_blank"
            rel="noopener"
            class="text-primary hover:underline flex items-center gap-2"
          >
            <i class="pi pi-list" /> {{ t('dashboard.tools.serilog') }}
          </a>
        </div>
      </template>
    </Card>
  </section>
</template>
